/**
 * missionModel.js — adaptador do domínio de execução para o Mission Control.
 *
 * Função PURA: recebe o status da execução (app.executions), os eventos da
 * timeline (`GET /executions/{id}/trace` → events[], ordenados por `seq`) e o
 * estado da entrega (`GET /executions/{id}/delivery-status`) e devolve um
 * SNAPSHOT visual: estado (6 estados do contrato), as 4 macroetapas com status e
 * detalhe, a sequência monotônica e a contagem de agentes ativos.
 *
 * O mapeamento completo está documentado em ./INTEGRACAO.md. Nada aqui avança por
 * temporizador: só eventos do domínio mudam o snapshot.
 */

export const MACROETAPAS = Object.freeze([
  { id: 'planning', label: 'Planejamento' },
  { id: 'development', label: 'Desenvolvimento' },
  { id: 'validation', label: 'Validação' },
  { id: 'deploy', label: 'Deploy' },
]);

export const ESTADOS = Object.freeze(['idle', 'running', 'approval', 'paused', 'completed', 'error']);
export const STATUS_DE_ETAPA = Object.freeze(['pending', 'running', 'approval', 'paused', 'completed', 'error', 'skipped']);

/** Velocidade de órbita/rotação por estado (cena WebGL). */
export const MISSION_SPEED = Object.freeze({
  running: 1, idle: 0.22, completed: 0.22, approval: 0, paused: 0, error: 0,
});

/** Janela de "agente em execução" (mesma do backend: _JANELA_AGENTE_ATIVO_MIN). */
export const JANELA_AGENTE_ATIVO_MS = 10 * 60 * 1000;

/**
 * Status de `app.executions` → estado visual.
 * O domínio não tem "paused": `paused` só existiria se o backend o expusesse.
 */
export function estadoVisual(execStatus) {
  const s = String(execStatus ?? '').toLowerCase().replace(/-/g, '_');
  if (!s) return 'idle';
  if (s === 'running' || s === 'pending') return 'running';
  if (s === 'gate_waiting' || s === 'contract_blocked') return 'approval';
  if (s === 'completed' || s === 'done') return 'completed';
  if (s === 'failed' || s === 'cancelled' || s === 'sla_expired') return 'error';
  if (s === 'paused') return 'paused';
  return 'idle';
}

// Fase (payload.phase / step) → macroetapa. Fonte: ExecutionWorkflow
// (apps/workers/navaflow_workers/workflows.py) e as fases publicadas pelos workers.
const FASE_PARA_MACRO = {
  // Planejamento: load_context → organize_squad → extract_requirements → Gate A →
  // ideation → design_plan → build_manufacturing_contract → Gate B
  inicio: 'planning', load_context: 'planning', squad: 'planning', organize_squad: 'planning',
  requisitos: 'planning', extract_requirements: 'planning', gate_a: 'planning',
  ideacao: 'planning', ideation: 'planning', design: 'planning', design_plan: 'planning',
  contrato: 'planning', build_manufacturing_contract: 'planning', gate_b: 'planning',
  // Desenvolvimento: layout_preview + camadas do codegen (e a remediação)
  fase_3: 'development', layout: 'development', layout_preview: 'development',
  codegen: 'development', remediacao: 'development', fase_4: 'development', execucao: 'development',
  // Validação: revisões, verify, runtime/E2E, blueprint, cobertura, garantia, compliance
  verify: 'validation', runtime: 'validation', e2e: 'validation', cobertura: 'validation',
  compliance: 'validation', compliance_audit: 'validation', blueprint: 'validation',
  garantia: 'validation', revisao: 'validation',
  // Deploy: entrega pendente ("Enviar ao GitHub") → PR → checks do PR
  entrega: 'deploy', pr: 'deploy', open_pull_request: 'deploy', push: 'deploy', delivery: 'deploy',
};

const EVENTO_PARA_MACRO = {
  gate_a_decision: 'planning', gate_b_decision: 'planning',
  pr_created: 'deploy', delivery_pushed: 'deploy',
};

// Dentro do codegen, o verify/runtime/E2E/garantia publicam com phase="codegen":
// a mensagem identifica a VALIDAÇÃO (rodadas do verify, runtime, E2E, garantia).
const VALIDACAO_NO_CODEGEN = /\b(verify|verifica[cç][aã]o|runtime|e2e|garantia|revis[aã]o de (seguran[cç]a|acessibilidade)|confer[eê]ncia de qa|compliance)\b/i;

/** Macroetapa de um evento da timeline (ou null quando não se aplica). */
export function macroDoEvento(ev) {
  if (!ev) return null;
  const tipo = String(ev.event_type ?? ev.type ?? '');
  if (EVENTO_PARA_MACRO[tipo]) return EVENTO_PARA_MACRO[tipo];
  const fase = String(ev.phase ?? '').toLowerCase();
  const macro = FASE_PARA_MACRO[fase] ?? null;
  if (macro === 'development' && VALIDACAO_NO_CODEGEN.test(String(ev.message ?? ''))) return 'validation';
  return macro;
}

const DETALHE_PADRAO = {
  pending: 'Aguardando início',
  running: 'Em execução',
  approval: 'Aguardando aprovação',
  paused: 'Pausado',
  completed: 'Concluído',
  error: 'Falha na execução',
  skipped: 'Não se aplica',
};

export function detalhePadrao(status) {
  return DETALHE_PADRAO[status] ?? DETALHE_PADRAO.pending;
}

function limpar(msg) {
  return String(msg ?? '').replace(/^\[[^\]]+\]\s*/, '').replace(/\s+/g, ' ').trim().slice(0, 140);
}

/**
 * Estado da macroetapa Deploy a partir do delivery-status (entregas do codegen):
 *  awaiting_approval → aprovação ("Enviar ao GitHub"); pushed → concluída (PR aberto);
 *  failed → erro. Sem entregas → null (decide pelos eventos).
 */
function deployPelaEntrega(delivery) {
  if (!delivery) return null;
  const aguardando = Number(delivery.awaiting_approval) || 0;
  const enviados = Number(delivery.pushed) || 0;
  const falhas = Number(delivery.failed) || 0;
  if (falhas > 0) return { status: 'error', detail: `${falhas} envio(s) com falha` };
  if (aguardando > 0) return { status: 'approval', detail: 'Aguardando “Enviar ao GitHub”' };
  if (enviados > 0) return { status: 'completed', detail: enviados === 1 ? 'PR aberto' : `${enviados} PRs abertos` };
  return null;
}

/**
 * Snapshot visual de UMA execução.
 *
 * @param {object} p
 * @param {string|null} p.executionId
 * @param {string|null} p.execStatus    status de app.executions
 * @param {Array} [p.events]            eventos da timeline (com seq)
 * @param {object|null} [p.delivery]    GET /executions/{id}/delivery-status
 * @param {number|null} [p.activeAgents] contagem vinda do backend (opcional)
 * @param {number} [p.now]              relógio (testes)
 */
export function derivarMissao({
  executionId = null, execStatus = null, events = [], delivery = null, activeAgents = null, now = Date.now(),
} = {}) {
  const lista = Array.isArray(events) ? events.filter(Boolean) : [];
  const ordenados = [...lista].sort((a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0));
  const sequence = ordenados.reduce((m, e) => Math.max(m, Number.isInteger(Number(e.seq)) ? Number(e.seq) : 0), 0);
  const state = executionId ? estadoVisual(execStatus) : 'idle';

  // Macroetapas alcançadas e a ÚLTIMA (atual) pela ordem dos eventos.
  const alcancadas = new Set();
  const ultimoDetalhe = {};
  let atual = null;
  for (const ev of ordenados) {
    const macro = macroDoEvento(ev);
    if (!macro) continue;
    alcancadas.add(macro);
    atual = macro;
    const msg = limpar(ev.message);
    if (msg) ultimoDetalhe[macro] = msg;
  }
  const idxAtual = atual ? MACROETAPAS.findIndex((m) => m.id === atual) : (state === 'idle' ? -1 : 0);

  const stages = MACROETAPAS.map((m, i) => {
    let status;
    if (state === 'idle') status = 'pending';
    else if (i < idxAtual) status = alcancadas.has(m.id) ? 'completed' : 'skipped';
    else if (i === idxAtual) {
      status = state === 'running' ? 'running'
        : state === 'approval' ? 'approval'
          : state === 'paused' ? 'paused'
            : state === 'error' ? 'error'
              : 'completed';
    } else {
      status = state === 'completed' ? 'skipped' : 'pending';
    }
    return { id: m.id, status, detail: status === 'running' || status === 'error' || status === 'approval'
      ? (ultimoDetalhe[m.id] || detalhePadrao(status)) : detalhePadrao(status) };
  });

  // Deploy: a entrega pendente/enviada é o dado de domínio mais fiel.
  const dep = deployPelaEntrega(delivery);
  if (dep && state !== 'idle') {
    const d = stages[3];
    d.status = dep.status;
    d.detail = dep.detail;
    // Entrega existe → desenvolvimento e validação já passaram por aqui.
    for (const s of stages.slice(0, 3)) {
      if (s.status === 'pending' || s.status === 'running') s.status = state === 'error' && s.status === 'running' ? 'error' : 'completed';
      if (s.status === 'completed') s.detail = detalhePadrao('completed');
    }
  }

  // Agentes ativos: valor do backend quando veio; senão, agentes distintos com
  // evento na janela (só com a execução rodando).
  let agentes = Number.isInteger(activeAgents) && activeAgents >= 0 ? activeAgents : null;
  if (agentes == null) {
    if (state !== 'running') agentes = 0;
    else {
      const ids = new Set();
      for (const ev of ordenados) {
        const t = Date.parse(ev.occurred_at ?? '');
        if (ev.agent_id && Number.isFinite(t) && now - t <= JANELA_AGENTE_ATIVO_MS) ids.add(String(ev.agent_id));
      }
      agentes = ids.size;
    }
  }

  const ultimo = ordenados.at(-1);
  return {
    runId: executionId ?? 'nenhuma',
    sequence,
    state,
    activeAgents: agentes,
    stages,
    updatedAt: ultimo?.occurred_at && Number.isFinite(Date.parse(ultimo.occurred_at))
      ? new Date(ultimo.occurred_at).toISOString() : new Date(now).toISOString(),
  };
}

/** Valida o snapshot (contrato do pacote — state-contract.js). Lança TypeError. */
export function validarSnapshot(s) {
  if (!s || typeof s.runId !== 'string' || !Number.isInteger(s.sequence) || s.sequence < 0) {
    throw new TypeError('runId e sequence são obrigatórios');
  }
  if (!ESTADOS.includes(s.state)) throw new TypeError('Estado de execução inválido');
  if (!Number.isInteger(s.activeAgents) || s.activeAgents < 0) throw new TypeError('Contagem inválida: activeAgents');
  if (!Array.isArray(s.stages) || s.stages.length !== 4) throw new TypeError('As quatro etapas são obrigatórias');
  if (new Set(s.stages.map((x) => x.id)).size !== 4) throw new TypeError('Etapa duplicada');
  for (const st of s.stages) {
    if (!MACROETAPAS.some((m) => m.id === st.id) || !STATUS_DE_ETAPA.includes(st.status)) throw new TypeError('Etapa inválida');
  }
  if (!Number.isFinite(Date.parse(s.updatedAt))) throw new TypeError('updatedAt deve ser uma data ISO válida');
  return s;
}

/**
 * Store mínimo por execução (contrato do pacote): `apply` ignora snapshot de OUTRA
 * execução e sequência MENOR que a atual (evento atrasado não regride a tela);
 * na mesma sequência, só aceita um snapshot mais novo (`updatedAt` posterior ou
 * mudança de estado/entrega vinda de outra fonte com o mesmo seq). `reset` troca
 * explicitamente a execução selecionada.
 */
export function criarStoreDaMissao(inicial) {
  let atual = validarSnapshot(inicial);
  return {
    get: () => atual,
    apply(next) {
      validarSnapshot(next);
      if (next.runId !== atual.runId) return false;
      if (next.sequence < atual.sequence) return false;
      if (next.sequence === atual.sequence && JSON.stringify(next) === JSON.stringify(atual)) return false;
      atual = next;
      return true;
    },
    reset(next) {
      atual = validarSnapshot(next);
      return true;
    },
  };
}
